/**
 * Links to a board or one of its tasks (owner, 05.10.2026: "Copy link" should
 * copy only the link, and opening it should open the board with the task in
 * its modal).
 *
 *   apiboo://boards/<scope>/<boardId>                — the board (the MCP resource URI)
 *   apiboo://boards/<scope>/<boardId>/tasks/<taskId> — the board, with that task open
 *
 * <scope> is `personal` or `team:<id>`, percent-encoded as in mcp/resources.js.
 * The desktop app is registered for apiboo:// — Rust (board_link.rs) reads the
 * link into the deep-link inbox; deepLinks/contentLinks.js hands it to
 * openBoardLink once the user is signed in. `quietMiss`: the caller says
 * "not found" itself (it retries while the account's data settles).
 */
import { boardUri } from '../mcp/resources';
import { setActiveTab } from '../store/uiModalStore';
import { openTaskManagerBoardTab } from '../store/tabsStore';
import { requestTaskFocus } from '../extensions/bundled/task-manager/boardsUiStore';
import { useNotificationStore } from '../store/notificationStore';

const enc = (s) => String(s).replace(/[^A-Za-z0-9\-_.~]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`);

/** The link that opens a task: its board's URI plus `/tasks/<id>`. */
export function taskLink({ teamId = null, boardId, taskId }) {
  return `${boardUri(teamId != null ? `team:${teamId}` : 'personal', boardId)}/tasks/${enc(taskId)}`;
}

/**
 * Open a board (and a task in its modal): the Task Manager, the board's tab,
 * then the task — the board tab collects the request once it has loaded
 * (pendingTaskFocus), as a chat share card's "Open" does. The board is read
 * first, by id alone (personal, then every team): its name labels the tab, and
 * a board this account cannot see is said so instead of opening an empty tab.
 */
export async function openBoardLink({ boardId, taskId = null, quietMiss = false } = {}) {
  if (!boardId) return false;
  const { readBoard } = await import('../extensions/bundled/task-manager/boardsStore');
  const board = await readBoard(String(boardId)).catch(() => null);
  if (!board) {
    if (quietMiss) return false;
    useNotificationStore.getState().error('This board is not on this device, or not in a scope you can open.', 'Board not found');
    return false;
  }
  setActiveTab('task-manager.panel');
  openTaskManagerBoardTab({ boardId: String(boardId), label: board.name || 'Board' });
  if (taskId) requestTaskFocus(String(boardId), String(taskId));
  return true;
}
